/**
 * Slack channel name resolution for the `alert` / `schedule` CLI groups.
 *
 * Alerts store a raw Slack channel ID (e.g. `C08K28GQ4HK`). For human-readable
 * output we resolve IDs → names; for friendlier input we resolve names → IDs.
 * Both directions come from one `GET /integrations/slack/channels` call,
 * memoized per process so a multi-row `list` only fetches once.
 *
 * Resolution is best-effort: any failure (no Slack connection, network) yields
 * empty maps so callers fall back to the raw ID and never break.
 *
 * @module slack-channels
 */

import { apiRequest } from './cli-helpers.js';

const SLACK_CHANNELS_PATH = '/integrations/slack/channels';

export interface SlackChannelMaps {
  /** channel id → channel name (no leading `#`) */
  idToName: Map<string, string>;
  /** lowercased channel name → channel id */
  nameToId: Map<string, string>;
}

interface SlackChannel {
  id?: string;
  name?: string;
}

let cached: Promise<SlackChannelMaps> | null = null;

/**
 * Fetch the org's Slack channels and build id↔name lookup maps. Memoized for
 * the lifetime of the process; any failure resolves to empty maps.
 */
export function loadSlackChannels(apiUrl: string, token: string): Promise<SlackChannelMaps> {
  if (cached) return cached;
  cached = (async () => {
    const idToName = new Map<string, string>();
    const nameToId = new Map<string, string>();
    const res = await apiRequest<{ ok: boolean; error?: string; channels?: SlackChannel[] }>(
      apiUrl,
      token,
      'GET',
      SLACK_CHANNELS_PATH
    );
    if (res.ok && Array.isArray(res.channels)) {
      for (const ch of res.channels) {
        if (!ch.id || !ch.name) continue;
        idToName.set(ch.id, ch.name);
        // First writer wins on duplicate names — harmless for our use.
        const key = ch.name.toLowerCase();
        if (!nameToId.has(key)) nameToId.set(key, ch.id);
      }
    }
    return { idToName, nameToId };
  })();
  return cached;
}

/** True when the value looks like a Slack channel/group ID (C…/G…), not a name. */
export function looksLikeChannelId(value: string): boolean {
  return /^[CG][A-Z0-9]{6,}$/.test(value);
}

/** Render a slack channel for display: `#name (C…)` when known, else the raw id. */
export function formatSlackChannel(channelId: string, idToName?: Map<string, string>): string {
  const name = idToName?.get(channelId);
  return name ? `#${name} (${channelId})` : channelId;
}
